import { useQuery } from '@tanstack/react-query';
import type { CouponDefinition } from '@/lib/api-admin';
import type { ApiMeta } from '@/lib/http-core';
import { apiClient } from '@/lib/apiClient';

export interface CouponsQueryParams {
  page?: number;
  size?: number;
  keyword?: string | undefined;
}

export interface CouponsPage {
  items: ReadonlyArray<CouponDefinition>;
  meta: ApiMeta;
}

export const couponsQueryKey = (params: CouponsQueryParams) =>
  ['admin', 'coupons', params] as const;

export const useCouponsQuery = (params: CouponsQueryParams) =>
  useQuery<CouponsPage>({
    queryKey: couponsQueryKey(params),
    queryFn: async () => {
      const query: Record<string, string | number> = {};
      // UI page 는 1-based, 쿠폰 목록 BE 는 0-based Spring Pageable.
      if (params.page != null) query.page = Math.max(0, params.page - 1);
      if (params.size != null) query.size = params.size;
      if (params.keyword) query.keyword = params.keyword;
      const [data, meta] = await apiClient.request('get', '/api/admin/v1/coupons', {
        params: { query },
      });
      const payload = data as {
        items?: CouponDefinition[];
        content?: CouponDefinition[];
        totalElements?: number;
        number?: number;
        size?: number;
      };
      const items = payload.items ?? payload.content ?? [];
      const mergedMeta: ApiMeta = meta?.pagination
        ? meta
        : {
            ...meta,
            pagination: {
              page: (payload.number ?? 0) + 1,
              size: payload.size ?? items.length,
              totalItems: payload.totalElements ?? items.length,
              totalPages:
                payload.totalElements != null && payload.size
                  ? Math.ceil(payload.totalElements / payload.size)
                  : 1,
            },
          };
      return { items, meta: mergedMeta };
    },
  });
